import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowDownUp, TrendingUp, TrendingDown, DollarSign,
  PiggyBank, Loader2, RefreshCw, Zap, ArrowUpRight,
  ArrowDownRight, BarChart3, Lightbulb, ChevronRight
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
  AreaChart, Area
} from "recharts";

const fmt = (v) => {
  const n = parseFloat(v) || 0;
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtFull = (v) =>
  parseFloat(v || 0).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });

const EXPENSE_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#8b5cf6", "#ec4899", "#64748b", "#14b8a6", "#f43f5e",
];
const INCOME_COLORS = [
  "#10b981", "#06b6d4", "#3b82f6", "#8b5cf6", "#14b8a6",
  "#22d3ee", "#34d399", "#60a5fa", "#a78bfa", "#2dd4bf",
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
      <p className="text-zinc-400 mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400 capitalize">{p.name || p.dataKey}:</span>
          <span className="text-white font-mono">{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-black/95 border border-white/10 rounded-sm p-3 text-xs">
      <p className="text-white font-medium mb-1 capitalize">{d.name}</p>
      <p className="text-zinc-400">{fmtFull(d.value)} ({d.payload.pct}%)</p>
    </div>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold font-mono mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-zinc-600 mt-1">{sub}</p>}
        </div>
        <div className={`w-9 h-9 rounded flex items-center justify-center ${color.includes("emerald") ? "bg-emerald-500/10" : color.includes("red") ? "bg-red-500/10" : color.includes("cyan") ? "bg-cyan-500/10" : "bg-white/5"}`}>
          <Icon className={`w-4.5 h-4.5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function CashFlow() {
  const { api } = useAuth();
  const [monthly, setMonthly] = useState(null);
  const [categories, setCategories] = useState(null);
  const [trends, setTrends] = useState(null);
  const [aiRecs, setAiRecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, cRes, tRes] = await Promise.all([
        api.get("/cashflow/monthly"),
        api.get("/cashflow/categories"),
        api.get("/cashflow/trends"),
      ]);
      setMonthly(mRes.data);
      setCategories(cRes.data);
      setTrends(tRes.data);
    } catch {
      toast.error("Failed to load cash flow data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAiRecs = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/cashflow/ai-recommendations");
      setAiRecs(res.data);
      toast.success("AI analysis complete");
    } catch {
      toast.error("Failed to generate AI recommendations");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Cash Flow">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  const summary = monthly?.summary || {};
  const months = monthly?.months || [];
  const expCats = categories?.expense_categories || [];
  const incCats = categories?.income_categories || [];
  const trendData = trends?.trends || [];
  const hasData = months.length > 0;

  const chartData = months.map((m) => ({
    month: new Date(m.month + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
    Income: m.income,
    Expenses: m.expense,
    Net: m.net,
  }));

  return (
    <Layout title="Cash Flow">
      <div className="space-y-6" data-testid="cashflow-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Monthly Cash Flow</h1>
            <p className="text-sm text-zinc-500 mt-1">Income vs. expenses analysis</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={loadAiRecs}
              disabled={aiLoading || !hasData}
              variant="outline"
              size="sm"
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300 rounded-sm"
              data-testid="ai-recommend-btn"
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
              AI Insights
            </Button>
            <Button
              onClick={loadData}
              variant="outline"
              size="sm"
              className="border-white/10 text-zinc-400 hover:text-white rounded-sm"
              data-testid="refresh-cf-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {!hasData ? (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="py-16 text-center">
              <ArrowDownUp className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No cash flow data yet</p>
              <p className="text-zinc-600 text-xs mt-1">Add income and expenses to see your monthly analysis</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Avg Monthly Income"
                value={fmtFull(summary.avg_monthly_income)}
                sub={`${summary.num_months} months tracked`}
                icon={ArrowUpRight}
                color="text-emerald-400"
                testId="stat-avg-income"
              />
              <StatCard
                label="Avg Monthly Expenses"
                value={fmtFull(summary.avg_monthly_expense)}
                sub={`${fmtFull(summary.total_expense)} total`}
                icon={ArrowDownRight}
                color="text-red-400"
                testId="stat-avg-expense"
              />
              <StatCard
                label="Avg Net Cash Flow"
                value={fmtFull(summary.avg_monthly_net)}
                sub={summary.avg_monthly_net >= 0 ? "Positive flow" : "Negative flow"}
                icon={ArrowDownUp}
                color={summary.avg_monthly_net >= 0 ? "text-emerald-400" : "text-red-400"}
                testId="stat-avg-net"
              />
              <StatCard
                label="Savings Rate"
                value={`${summary.avg_savings_rate}%`}
                sub={summary.avg_savings_rate >= 20 ? "Healthy" : summary.avg_savings_rate >= 10 ? "Fair" : "Needs work"}
                icon={PiggyBank}
                color={summary.avg_savings_rate >= 20 ? "text-cyan-400" : summary.avg_savings_rate >= 10 ? "text-amber-400" : "text-red-400"}
                testId="stat-savings-rate"
              />
            </div>

            {/* Monthly Chart */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-cyan-400" /> Income vs. Expenses Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" data-testid="monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={fmt} width={55} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: "#71717a" }} />
                      <Bar dataKey="Income" fill="#10b981" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Expenses" fill="#ef4444" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Net Cash Flow Trend */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" /> Net Cash Flow Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[220px]" data-testid="net-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="netGradPos" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717a" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#71717a" }} tickFormatter={fmt} width={55} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="Net" stroke="#10b981" fill="url(#netGradPos)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Category Breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Expense Categories */}
              <Card className="bg-[#0A0A0A] border-red-500/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <ArrowDownRight className="w-4 h-4 text-red-400" /> Expense Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {expCats.length === 0 ? (
                    <p className="text-sm text-zinc-600 text-center py-4">No expense data</p>
                  ) : (
                    <div className="flex gap-6">
                      <div className="w-40 h-40 flex-shrink-0" data-testid="expense-pie">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={expCats.slice(0, 8)}
                              cx="50%" cy="50%"
                              innerRadius={30} outerRadius={65}
                              dataKey="total" nameKey="category"
                              stroke="none"
                            >
                              {expCats.slice(0, 8).map((_, i) => (
                                <Cell key={i} fill={EXPENSE_COLORS[i % EXPENSE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<PieTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-2 overflow-y-auto max-h-[200px]">
                        {expCats.map((cat, i) => (
                          <div key={cat.category} className="flex items-center gap-2 text-xs">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: EXPENSE_COLORS[i % EXPENSE_COLORS.length] }} />
                            <span className="text-zinc-400 capitalize flex-1 truncate">{cat.category}</span>
                            <span className="text-white font-mono">{fmtFull(cat.total)}</span>
                            <span className="text-zinc-600 w-10 text-right">{cat.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Income Categories */}
              <Card className="bg-[#0A0A0A] border-emerald-500/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" /> Income Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {incCats.length === 0 ? (
                    <p className="text-sm text-zinc-600 text-center py-4">No income data</p>
                  ) : (
                    <div className="flex gap-6">
                      <div className="w-40 h-40 flex-shrink-0" data-testid="income-pie">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={incCats.slice(0, 8)}
                              cx="50%" cy="50%"
                              innerRadius={30} outerRadius={65}
                              dataKey="total" nameKey="category"
                              stroke="none"
                            >
                              {incCats.slice(0, 8).map((_, i) => (
                                <Cell key={i} fill={INCOME_COLORS[i % INCOME_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<PieTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-2 overflow-y-auto max-h-[200px]">
                        {incCats.map((cat, i) => (
                          <div key={cat.category} className="flex items-center gap-2 text-xs">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: INCOME_COLORS[i % INCOME_COLORS.length] }} />
                            <span className="text-zinc-400 capitalize flex-1 truncate">{cat.category}</span>
                            <span className="text-white font-mono">{fmtFull(cat.total)}</span>
                            <span className="text-zinc-600 w-10 text-right">{cat.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Spending Trends */}
            {trendData.length > 0 && (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader>
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-amber-400" /> Month-over-Month Spending Trends
                    <span className="text-xs text-zinc-600 font-normal ml-2">
                      {trends?.previous_month} vs {trends?.latest_month}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Top Increases */}
                    {trends?.top_increases?.length > 0 && (
                      <div>
                        <p className="text-xs text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ArrowUpRight className="w-3 h-3" /> Increased Spending
                        </p>
                        <div className="space-y-2">
                          {trends.top_increases.map((t) => (
                            <div key={t.category} className="flex items-center justify-between bg-red-500/5 border border-red-500/10 px-3 py-2 rounded-sm">
                              <span className="text-sm text-zinc-300 capitalize">{t.category}</span>
                              <div className="text-right">
                                <span className="text-xs text-red-400 font-mono">+{fmtFull(t.change)}</span>
                                <span className="text-xs text-zinc-600 ml-2">({t.pct_change > 0 ? "+" : ""}{t.pct_change}%)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Top Decreases */}
                    {trends?.top_decreases?.length > 0 && (
                      <div>
                        <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ArrowDownRight className="w-3 h-3" /> Decreased Spending
                        </p>
                        <div className="space-y-2">
                          {trends.top_decreases.map((t) => (
                            <div key={t.category} className="flex items-center justify-between bg-emerald-500/5 border border-emerald-500/10 px-3 py-2 rounded-sm">
                              <span className="text-sm text-zinc-300 capitalize">{t.category}</span>
                              <div className="text-right">
                                <span className="text-xs text-emerald-400 font-mono">{fmtFull(t.change)}</span>
                                <span className="text-xs text-zinc-600 ml-2">({t.pct_change}%)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Details Table */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-zinc-400" /> Monthly Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto" data-testid="monthly-table">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-zinc-500 py-2 px-3 font-medium">Month</th>
                        <th className="text-right text-zinc-500 py-2 px-3 font-medium">Income</th>
                        <th className="text-right text-zinc-500 py-2 px-3 font-medium">Expenses</th>
                        <th className="text-right text-zinc-500 py-2 px-3 font-medium">Net</th>
                        <th className="text-right text-zinc-500 py-2 px-3 font-medium">Savings %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {months.slice().reverse().map((m) => (
                        <tr key={m.month} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-2.5 px-3 text-zinc-300 font-mono">
                            {new Date(m.month + "-01").toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                          </td>
                          <td className="py-2.5 px-3 text-right text-emerald-400 font-mono">{fmtFull(m.income)}</td>
                          <td className="py-2.5 px-3 text-right text-red-400 font-mono">{fmtFull(m.expense)}</td>
                          <td className={`py-2.5 px-3 text-right font-mono ${m.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {m.net >= 0 ? "+" : ""}{fmtFull(m.net)}
                          </td>
                          <td className={`py-2.5 px-3 text-right font-mono ${m.savings_rate >= 20 ? "text-cyan-400" : m.savings_rate >= 0 ? "text-amber-400" : "text-red-400"}`}>
                            {m.savings_rate.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* AI Recommendations */}
            {aiRecs && (
              <Card className="bg-[#0A0A0A] border-cyan-500/10 rounded-none" data-testid="ai-recommendations">
                <CardHeader>
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-cyan-400" /> AI Savings Recommendations
                    {aiRecs.health_score && (
                      <span className={`ml-auto text-xs font-mono px-2 py-0.5 rounded-full ${
                        aiRecs.health_score >= 7 ? "bg-emerald-500/10 text-emerald-400" :
                        aiRecs.health_score >= 4 ? "bg-amber-500/10 text-amber-400" :
                        "bg-red-500/10 text-red-400"
                      }`}>
                        Health: {aiRecs.health_score}/10
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {aiRecs.summary && (
                    <p className="text-sm text-zinc-300 leading-relaxed">{aiRecs.summary}</p>
                  )}

                  {aiRecs.savings_potential > 0 && (
                    <div className="bg-cyan-500/5 border border-cyan-500/10 p-3 rounded-sm">
                      <p className="text-xs text-cyan-400 uppercase tracking-wider">Estimated Monthly Savings Potential</p>
                      <p className="text-xl font-bold font-mono text-cyan-300 mt-1">{fmtFull(aiRecs.savings_potential)}</p>
                    </div>
                  )}

                  {aiRecs.recommendations?.length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Category Recommendations</p>
                      <div className="space-y-2">
                        {aiRecs.recommendations.map((rec, i) => (
                          <div key={i} className="bg-white/[0.02] border border-white/5 p-3 rounded-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-white capitalize font-medium">{rec.category}</span>
                              <span className="text-xs text-emerald-400 font-mono">Save {fmtFull(rec.savings)}/mo</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-zinc-500 mb-1.5">
                              <span>Current: {fmtFull(rec.current_spend)}</span>
                              <ChevronRight className="w-3 h-3" />
                              <span>Target: {fmtFull(rec.suggested_target)}</span>
                            </div>
                            <p className="text-xs text-zinc-400">{rec.tip}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiRecs.quick_wins?.length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Quick Wins</p>
                      <div className="space-y-1.5">
                        {aiRecs.quick_wins.map((win, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                            <Zap className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                            {win}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
